// Chat state management hook with Kaycoo agent execution + session persistence
// @requirement NFR-02: 30-minute timeout with auto-save
// @requirement SPEC-IRO-001/UX-03: Session recovery after browser close

import { useState, useCallback, useEffect, useRef } from 'react'
import type { ChatMessage } from '@/types/issue'
import { executeAgent } from '@/modules/agents/agent-client'
import { supabase } from '@/lib/supabase'
import type { Json } from '@/types/database.generated'

interface UseChatParams {
  agentId: string
  sessionType?: string
  sessionKey?: string
  initialMessages?: ChatMessage[]
  extractedData?: Record<string, unknown>
  completeness?: number
  enablePersistence?: boolean
  contextProvider?: () => string
  onMessagePersisted?: (userMsg: ChatMessage, assistantMsg: ChatMessage) => void
}

interface UseChatReturn {
  messages: ChatMessage[]
  isTyping: boolean
  error: Error | null
  sessionId: string | null
  sendMessage: (content: string) => Promise<void>
  clearMessages: () => void
  resetMessages: (newMessages: ChatMessage[]) => void
  cancelStreaming: () => void
  recoverSession: (sessionId: string) => Promise<void>
}

const AUTO_SAVE_INTERVAL = 30000

export function useChat({
  agentId,
  sessionType = 'issue_recording',
  sessionKey,
  initialMessages = [],
  extractedData = {},
  completeness = 0,
  enablePersistence = true,
  contextProvider,
  onMessagePersisted,
}: UseChatParams): UseChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages)
  const [isTyping, setIsTyping] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [localExtractedData, setLocalExtractedData] = useState(extractedData)
  const [localCompleteness, setLocalCompleteness] = useState(completeness)
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null)
  const abortedRef = useRef(false)
  const contextProviderRef = useRef(contextProvider)
  const onMessagePersistedRef = useRef(onMessagePersisted)

  // Keep refs in sync with latest values
  useEffect(() => { contextProviderRef.current = contextProvider }, [contextProvider])
  useEffect(() => { onMessagePersistedRef.current = onMessagePersisted }, [onMessagePersisted])

  // Initialize or recover session on mount
  useEffect(() => {
    if (!enablePersistence) return

    const initSession = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) return

        const { data: userRow } = await supabase
          .from('users')
          .select('organization_id')
          .eq('id', user.id)
          .single()

        if (!userRow?.organization_id) return

        const { data: newSessionId, error: sessionError } = await supabase
          .rpc('get_or_create_session', {
            p_user_id: user.id,
            p_organization_id: userRow.organization_id,
            p_session_type: sessionType,
          })

        if (sessionError) throw sessionError

        setSessionId(newSessionId as string)

        const { data: session } = await supabase
          .from('conversation_sessions')
          .select('*')
          .eq('id', newSessionId)
          .single()

        const history = session?.conversation_history as ChatMessage[] | null
        // Only recover session if no sessionKey (customer-specific sessions start fresh)
        if (!sessionKey && session && Array.isArray(history) && history.length > 0) {
          setMessages(history)
          setLocalExtractedData((session.extracted_data as Record<string, unknown>) || {})
          setLocalCompleteness(session.completeness || 0)
        }
      } catch (err) {
        console.error('Failed to initialize session:', err)
      }
    }

    initSession()
  }, [enablePersistence, sessionType, sessionKey])

  // Auto-save session state every 30 seconds
  useEffect(() => {
    if (!enablePersistence || !sessionId || messages.length === 0) return

    const saveSession = async () => {
      try {
        await supabase.rpc('save_session_state', {
          p_session_id: sessionId,
          p_conversation_history: messages as unknown as Json,
          p_extracted_data: localExtractedData as unknown as Json,
          p_completeness: localCompleteness,
        })
      } catch (err) {
        console.error('Auto-save failed:', err)
      }
    }

    const initialTimer = setTimeout(saveSession, 5000)
    autoSaveTimerRef.current = setInterval(saveSession, AUTO_SAVE_INTERVAL)

    return () => {
      clearTimeout(initialTimer)
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current)
      }
    }
  }, [enablePersistence, sessionId, messages, localExtractedData, localCompleteness])

  // Save on unmount
  useEffect(() => {
    if (!enablePersistence || !sessionId) return

    const handleBeforeUnload = async () => {
      if (messages.length > 0) {
        await supabase.rpc('save_session_state', {
          p_session_id: sessionId,
          p_conversation_history: messages as unknown as Json,
          p_extracted_data: localExtractedData as unknown as Json,
          p_completeness: localCompleteness,
        })
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [enablePersistence, sessionId, messages, localExtractedData, localCompleteness])

  const sendMessage = useCallback(
    async (content: string) => {
      const userMessage: ChatMessage = {
        role: 'user',
        content,
        timestamp: new Date().toISOString(),
      }

      setMessages((prev) => [...prev, userMessage])
      setIsTyping(true)
      setError(null)
      abortedRef.current = false

      try {
        // Build context prompt with conversation history and extracted context
        const contextPrefix = contextProviderRef.current ? contextProviderRef.current() : ''
        const recentMessages = messages.slice(-10)
        const historyText = recentMessages
          .map((m) => `${m.role}: ${m.content}`)
          .join('\n')

        const prompt = [contextPrefix, historyText, `user: ${content}`]
          .filter(Boolean)
          .join('\n')

        console.log('[useChat] Executing agent:', agentId)
        console.log('[useChat] Prompt:', prompt)

        const result = await executeAgent(agentId, { input: prompt })

        console.log('[useChat] Agent response:', result)
        console.log('[useChat] Response keys:', Object.keys(result))
        console.log('[useChat] result.response:', result.response)
        console.log('[useChat] result.result:', result.result)

        if (abortedRef.current) return

        const responseText = result.response || result.result || result.output || ''

        if (!responseText) {
          console.error('[useChat] Empty response from agent')
          console.error('[useChat] Full result object:', JSON.stringify(result, null, 2))
          throw new Error('Agent returned empty response. Please check agent configuration in Settings > Agents.')
        }

        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content: responseText,
          timestamp: new Date().toISOString(),
        }

        setMessages((prev) => [...prev, assistantMessage])
        onMessagePersistedRef.current?.(userMessage, assistantMessage)
      } catch (err) {
        if (!abortedRef.current) {
          const error = err as Error
          console.error('[useChat] Error:', error)
          setError(error)
        }
      } finally {
        setIsTyping(false)
      }
    },
    [agentId, messages]
  )

  const clearMessages = useCallback(() => {
    setMessages([])
    setLocalExtractedData({})
    setLocalCompleteness(0)
  }, [])

  const resetMessages = useCallback((newMessages: ChatMessage[]) => {
    setMessages(newMessages)
    setLocalExtractedData({})
    setLocalCompleteness(0)
    setSessionId(null)
  }, [])

  const cancelStreaming = useCallback(() => {
    abortedRef.current = true
    setIsTyping(false)
  }, [])

  const recoverSession = useCallback(async (recoverySessionId: string) => {
    const { data: session, error: err } = await supabase
      .from('conversation_sessions')
      .select('*')
      .eq('id', recoverySessionId)
      .single()

    if (err) throw err

    setSessionId(recoverySessionId)
    setMessages(session.conversation_history as unknown as ChatMessage[])
    setLocalExtractedData((session.extracted_data as Record<string, unknown>) || {})
    setLocalCompleteness(session.completeness || 0)
  }, [])

  return {
    messages,
    isTyping,
    error,
    sessionId,
    sendMessage,
    clearMessages,
    resetMessages,
    cancelStreaming,
    recoverSession,
  }
}
